import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ToolCard from "../components/ToolCard";
import ToolFAQ from "../components/ToolFAQ";
import ComparisonSection from "../components/ComparisonSection";
import MobileStickyBar from "../components/MobileStickyBar";
import { AdZone } from "../components/AdBanner";
import JsonLD, { buildSoftwareApp, buildOrganization, buildWebSite } from "../components/JsonLD";
import { useLanguage } from "../context/LanguageContext";
import { TOOLS_LIST, EXCEL_TOOLS_LIST, CATEGORIES, SITE_NAME, SITE_URL } from "../translations";
import { SEO_CONTENT } from "../seoContent";
import { useExitIntent } from "../hooks/useExitIntent";
import { useHreflang } from "../hooks/useHreflang";
import { buildLocalizedPath } from "../i18nConfig";
import { BLOG_POSTS } from "../data/blogContent";
import { Zap, Shield, Star, FileCheck, X, ArrowRight, FileText, Table2, FileType, ImageDown, ChevronRight } from "lucide-react";

const BLOG_CATEGORY_COLORS = {
  PDF: "bg-blue-50 text-blue-700",
  Excel: "bg-green-50 text-green-700",
  "File Management": "bg-purple-50 text-purple-700",
};
const LATEST_GUIDES_TITLE = { en: "Latest Guides", fr: "Derniers guides", es: "Últimas guías", pt: "Últimos guias", de: "Aktuelle Anleitungen" };
const VIEW_ALL_LABEL = { en: "View all articles →", fr: "Voir tous les articles →", es: "Ver todos los artículos →", pt: "Ver todos os artigos →", de: "Alle Artikel anzeigen →" };

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAT_ICONS = { FileText, Table2, FileType, ImageDown };

const HOME_META = {
  en: {
    title: "Free PDF & Excel Tools Online — Merge, Compress, Convert | FileNesto",
    description: "FileNesto offers 15 free online PDF and Excel tools. Merge PDFs, compress PDFs, convert Excel to CSV and more — no signup, no installation.",
  },
  fr: {
    title: "Outils PDF & Excel gratuits en ligne | FileNesto",
    description: "FileNesto — 15 outils gratuits en ligne : 12 outils PDF et 3 outils Excel. Fusionner, compresser, convertir et plus. Sans inscription, sans installation.",
  },
  es: {
    title: "Herramientas PDF y Excel gratuitas en línea | FileNesto",
    description: "FileNesto — 15 herramientas de archivos gratuitas: 12 herramientas PDF y 3 de Excel. Fusionar, comprimir, convertir y más. Sin registro necesario.",
  },
  pt: {
    title: "Ferramentas PDF e Excel gratuitas online | FileNesto",
    description: "FileNesto — 15 ferramentas de arquivo gratuitas: 12 ferramentas PDF e 3 de Excel. Mesclar, comprimir, converter e mais. Sem cadastro necessário.",
  },
  de: {
    title: "Kostenlose PDF- und Excel-Tools online | FileNesto",
    description: "FileNesto — 15 kostenlose Datei-Tools: 12 PDF-Tools und 3 Excel-Tools. Zusammenführen, komprimieren, konvertieren und mehr. Keine Registrierung erforderlich.",
  },
};

const FEATURES_I18N = {
  en: [
    { icon: <Zap className="w-6 h-6" />, color: "text-blue-600", bg: "bg-blue-50", title: "Ultra Fast", desc: "Processing in under 3 seconds" },
    { icon: <Shield className="w-6 h-6" />, color: "text-purple-600", bg: "bg-purple-50", title: "100% Secure", desc: "Files are never stored or shared" },
    { icon: <Star className="w-6 h-6" />, color: "text-green-600", bg: "bg-green-50", title: "Completely Free", desc: "All tools, no registration required" },
    { icon: <FileCheck className="w-6 h-6" />, color: "text-orange-600", bg: "bg-orange-50", title: "Future-ready", desc: "PDF today — Excel/Word/Image next" },
  ],
  fr: [
    { icon: <Zap className="w-6 h-6" />, color: "text-blue-600", bg: "bg-blue-50", title: "Ultra rapide", desc: "Traitement en moins de 3 secondes" },
    { icon: <Shield className="w-6 h-6" />, color: "text-purple-600", bg: "bg-purple-50", title: "100% Sécurisé", desc: "Vos fichiers ne sont jamais stockés" },
    { icon: <Star className="w-6 h-6" />, color: "text-green-600", bg: "bg-green-50", title: "Entièrement gratuit", desc: "Tous les outils, sans inscription" },
    { icon: <FileCheck className="w-6 h-6" />, color: "text-orange-600", bg: "bg-orange-50", title: "Évolutif", desc: "PDF aujourd'hui, Excel/Word/Image bientôt" },
  ],
  es: [
    { icon: <Zap className="w-6 h-6" />, color: "text-blue-600", bg: "bg-blue-50", title: "Ultra rápido", desc: "Procesamiento en menos de 3 segundos" },
    { icon: <Shield className="w-6 h-6" />, color: "text-purple-600", bg: "bg-purple-50", title: "100% Seguro", desc: "Tus archivos nunca se almacenan" },
    { icon: <Star className="w-6 h-6" />, color: "text-green-600", bg: "bg-green-50", title: "Completamente gratis", desc: "Todas las herramientas, sin registro" },
    { icon: <FileCheck className="w-6 h-6" />, color: "text-orange-600", bg: "bg-orange-50", title: "Preparado para el futuro", desc: "PDF hoy — Excel/Word/Imagen próximamente" },
  ],
  pt: [
    { icon: <Zap className="w-6 h-6" />, color: "text-blue-600", bg: "bg-blue-50", title: "Ultra rápido", desc: "Processamento em menos de 3 segundos" },
    { icon: <Shield className="w-6 h-6" />, color: "text-purple-600", bg: "bg-purple-50", title: "100% Seguro", desc: "Seus arquivos nunca são armazenados" },
    { icon: <Star className="w-6 h-6" />, color: "text-green-600", bg: "bg-green-50", title: "Completamente gratuito", desc: "Todas as ferramentas, sem cadastro" },
    { icon: <FileCheck className="w-6 h-6" />, color: "text-orange-600", bg: "bg-orange-50", title: "Preparado para o futuro", desc: "PDF hoje — Excel/Word/Imagem em breve" },
  ],
  de: [
    { icon: <Zap className="w-6 h-6" />, color: "text-blue-600", bg: "bg-blue-50", title: "Ultraschnell", desc: "Verarbeitung in unter 3 Sekunden" },
    { icon: <Shield className="w-6 h-6" />, color: "text-purple-600", bg: "bg-purple-50", title: "100% Sicher", desc: "Dateien werden nie gespeichert" },
    { icon: <Star className="w-6 h-6" />, color: "text-green-600", bg: "bg-green-50", title: "Völlig kostenlos", desc: "Alle Tools, keine Registrierung" },
    { icon: <FileCheck className="w-6 h-6" />, color: "text-orange-600", bg: "bg-orange-50", title: "Zukunftssicher", desc: "PDF heute — Excel/Word/Bild als nächstes" },
  ],
};

const FAQ_TITLE = { en: "Frequently Asked Questions", fr: "Questions fréquentes", es: "Preguntas frecuentes", pt: "Perguntas frequentes", de: "Häufig gestellte Fragen" };
const WHY_TITLE = { en: `Why choose ${SITE_NAME}?`, fr: `Pourquoi choisir ${SITE_NAME} ?`, es: `¿Por qué elegir ${SITE_NAME}?`, pt: `Por que escolher ${SITE_NAME}?`, de: `Warum ${SITE_NAME} wählen?` };
const PROCESSED_LABEL = { en: "files processed today", fr: "fichiers traités aujourd'hui", es: "archivos procesados hoy", pt: "arquivos processados hoje", de: "Dateien heute verarbeitet" };
const EXIT_INTENT = {
  en: { before: "Before you go...", try: "Try our PDF to Word converter", btn: "Try it" },
  fr: { before: "Avant de partir...", try: "Essayez notre outil PDF vers Word", btn: "Essayer" },
  es: { before: "Antes de irte...", try: "Prueba nuestro conversor PDF a Word", btn: "Probar" },
  pt: { before: "Antes de sair...", try: "Experimente nossa ferramenta PDF para Word", btn: "Experimentar" },
  de: { before: "Bevor Sie gehen...", try: "Probieren Sie unseren PDF-zu-Word-Konverter", btn: "Ausprobieren" },
};

export default function Home() {
  const { t, lang } = useLanguage();
  const location = useLocation();
  const [processedToday, setProcessedToday] = useState(null);
  const { showIntent, dismiss } = useExitIntent(5000);

  useHreflang("", "home");

  useEffect(() => {
    const meta = HOME_META[lang] || HOME_META.en;
    document.title = meta.title;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", meta.description);
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = `${SITE_URL}${location.pathname}`;
  }, [lang, location.pathname]);

  useEffect(() => {
    axios.get(`${API}/stats`).then((res) => setProcessedToday(res.data.today)).catch(() => {});
  }, []);

  const features = FEATURES_I18N[lang] || FEATURES_I18N.en;
  const homeFaqs = SEO_CONTENT.home?.[lang]?.faqs || SEO_CONTENT.home?.en?.faqs || [];

  return (
    <div className="min-h-screen bg-white page-fade-in">
      <JsonLD data={buildSoftwareApp()} />
      <JsonLD data={buildOrganization()} />
      <JsonLD data={buildWebSite()} />
      <Navbar />
      <main>

      {/* Exit Intent Banner */}
      {showIntent && (
        <div
          className="fixed top-4 left-1/2 -translate-x-1/2 z-50 max-w-sm w-full mx-4 bg-white rounded-2xl shadow-xl border border-blue-200 p-4 flex items-center gap-3 page-fade-in"
          data-testid="exit-intent"
        >
          <div className="flex-1">
            <p className="text-sm font-semibold text-slate-800">
              {(EXIT_INTENT[lang] || EXIT_INTENT.en).before}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              {(EXIT_INTENT[lang] || EXIT_INTENT.en).try}
            </p>
          </div>
          <Link
            to={buildLocalizedPath("pdf-to-word", lang)}
            onClick={dismiss}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 transition-colors flex-shrink-0"
          >
            {(EXIT_INTENT[lang] || EXIT_INTENT.en).btn} <ArrowRight className="w-3 h-3" />
          </Link>
          <button onClick={dismiss} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 flex-shrink-0" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-white via-blue-50/40 to-white pt-14 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {processedToday !== null && processedToday > 0 && (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-50 border border-green-200 text-green-700 text-xs font-medium mb-4" data-testid="processed-counter">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span>{processedToday.toLocaleString()} {PROCESSED_LABEL[lang] || PROCESSED_LABEL.en}</span>
            </div>
          )}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-xs font-medium mb-6">
            <Zap className="w-3.5 h-3.5" />
            <span>{t.hero.stats.tools} · {t.hero.stats.free}</span>
          </div>
          <h1 className="text-5xl sm:text-6xl font-bold text-slate-900 mb-4 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="hero-title">
            {t.hero.title}{" "}<span className="text-blue-600">{t.hero.titleHighlight}</span>
          </h1>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto mb-8">{t.hero.subtitle}</p>
          <Link
            to="#tools"
            onClick={(e) => { e.preventDefault(); document.getElementById("tools")?.scrollIntoView({ behavior: "smooth" }); }}
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-blue-600 text-white font-semibold text-base hover:bg-blue-700 active:scale-95 transition-all shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            data-testid="hero-cta-btn"
          >
            {t.hero.cta}
          </Link>
          {/* Trust badges */}
          <div className="flex flex-wrap items-center justify-center gap-5 mt-8">
            {([
              { en: "< 3 seconds", fr: "< 3 secondes", es: "< 3 segundos", pt: "< 3 segundos", de: "< 3 Sekunden", icon: "⚡" },
              { en: "No file storage", fr: "Aucun stockage", es: "Sin almacenamiento", pt: "Sem armazenamento", de: "Keine Speicherung", icon: "🔒" },
              { en: "No signup", fr: "Sans inscription", es: "Sin registro", pt: "Sem cadastro", de: "Keine Anmeldung", icon: "✅" },
            ]).map((b, i) => (
              <div key={i} className="flex items-center gap-1.5 text-sm text-slate-500">
                <span>{b.icon}</span><span>{b[lang] || b.en}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-14 bg-white border-t border-slate-100" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {t.categories.title}
            </h2>
            <p className="text-slate-500 text-sm">{t.categories.subtitle}</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {CATEGORIES.map((cat) => {
              const Icon = CAT_ICONS[cat.icon] || FileText;
              return (
                <Link
                  key={cat.slug}
                  to={buildLocalizedPath(cat.slug, lang)}
                  className={`group p-5 rounded-2xl border ${cat.live ? "border-slate-200 bg-white hover:border-blue-300 hover:shadow-md" : "border-slate-100 bg-slate-50/50 hover:border-slate-200"} transition-all text-left`}
                  data-testid={`category-card-${cat.slug}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${cat.color}15`, color: cat.color }}>
                      <Icon className="w-5 h-5" />
                    </div>
                    {cat.live ? (
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-green-700 bg-green-50 px-2 py-0.5 rounded-full">{t.categories.pdfBadge}</span>
                    ) : (
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">{t.categories.soonBadge}</span>
                    )}
                  </div>
                  <h3 className="font-semibold text-slate-900 text-base mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {t.nav[cat.labelKey]}
                  </h3>
                  <p className="text-xs text-slate-500 leading-relaxed mb-3">{t.categories[cat.descKey]}</p>
                  {cat.live && (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 group-hover:gap-2 transition-all">
                      {t.categories.explore} <ChevronRight className="w-3 h-3" />
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Tools Grid */}
      <section id="tools" className="py-16 bg-[#f5f5f5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="tools-section-title">
              {t.toolsSection.title}
            </h2>
            <p className="text-slate-600 text-base">{t.toolsSection.subtitle}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="tools-grid">
            {[...TOOLS_LIST, ...EXCEL_TOOLS_LIST].map((tool) => (
              <ToolCard key={tool.slug} slug={tool.slug} icon={tool.icon} color={tool.color} title={t.tools[tool.key]?.title || tool.key} description={t.tools[tool.key]?.description || ""} />
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 my-2">
        <AdZone />
      </div>

      {/* Features */}
      <section className="py-14 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="features-section">
              {WHY_TITLE[lang] || WHY_TITLE.en}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f, i) => (
              <div key={i} className="flex flex-col items-center gap-3 p-6 rounded-2xl bg-white border border-slate-100 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-200 text-center" data-testid={`feature-card-${i}`}>
                <div className={`w-12 h-12 rounded-xl ${f.bg} flex items-center justify-center`}>
                  <span className={f.color}>{f.icon}</span>
                </div>
                <h3 className="font-semibold text-slate-900 text-base" style={{ fontFamily: "'Outfit', sans-serif" }}>{f.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ComparisonSection />

      {/* FAQ */}
      {homeFaqs.length > 0 && (
        <section className="py-14 bg-[#f5f5f5] border-t border-slate-100">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="home-faq-title">
                {FAQ_TITLE[lang] || FAQ_TITLE.en}
              </h2>
            </div>
            <ToolFAQ faqs={homeFaqs} />
          </div>
        </section>
      )}

      {/* Latest Guides */}
      <section className="py-14 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {LATEST_GUIDES_TITLE[lang] || LATEST_GUIDES_TITLE.en}
            </h2>
            <Link to="/blog" className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors">
              {VIEW_ALL_LABEL[lang] || VIEW_ALL_LABEL.en}
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {BLOG_POSTS.slice(0, 3).map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                className="group p-5 bg-white rounded-2xl border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${BLOG_CATEGORY_COLORS[post.category] || "bg-slate-100 text-slate-600"}`}>
                    {post.category}
                  </span>
                  <span className="text-xs text-slate-500">{post.readingTime}</span>
                </div>
                <h3 className="text-sm font-semibold text-slate-900 leading-snug mb-2 group-hover:text-blue-600 transition-colors" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {post.title}
                </h3>
                <p className="text-xs text-slate-500 leading-relaxed line-clamp-2">{post.excerpt}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SEO Content Block */}
      <section className="py-14 bg-white border-t border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {SEO_CONTENT.home?.[lang]?.seoText ? (
            <div className="prose prose-slate max-w-none text-sm leading-relaxed text-slate-600" dangerouslySetInnerHTML={{ __html: SEO_CONTENT.home[lang].seoText }} />
          ) : lang === "fr" ? (
            <div className="prose prose-slate max-w-none text-sm leading-relaxed text-slate-600 space-y-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Outils en ligne gratuits — PDF, Excel et plus
              </h2>
              <p>FileNesto est une plateforme gratuite d'outils de conversion et d'édition de fichiers en ligne, disponible directement dans votre navigateur. Les 15 outils couvrent les formats PDF et Excel. Chaque traitement se fait entièrement en mémoire dans votre session — les fichiers sont supprimés immédiatement après votre téléchargement. Aucun compte, aucune installation, aucune donnée stockée sur nos serveurs.</p>
              <h3 className="text-lg font-semibold text-slate-800 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>12 outils PDF gratuits</h3>
              <p>FileNesto couvre l'ensemble des opérations PDF courantes. <strong>Fusionner PDF</strong> permet de combiner autant de documents que nécessaire en un seul fichier. <strong>Compresser PDF</strong> réduit la taille du fichier sans rééchantillonner les images. <strong>Diviser PDF</strong> extrait des pages individuelles ou des plages personnalisées dans une archive ZIP. <strong>PDF vers Word</strong>, <strong>PDF vers Excel</strong>, <strong>PDF vers Image</strong>, <strong>Aperçu PDF</strong>, <strong>Faire pivoter PDF</strong>, <strong>Supprimer des pages</strong> et <strong>Protéger PDF</strong> complètent la suite.</p>
              <h3 className="text-lg font-semibold text-slate-800 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>3 outils Excel gratuits</h3>
              <p><strong>Excel vers PDF</strong>, <strong>Excel vers CSV</strong> et <strong>CSV vers Excel</strong> couvrent les besoins de conversion les plus courants pour vos tableurs.</p>
            </div>
          ) : (
            <div className="prose prose-slate max-w-none text-sm leading-relaxed text-slate-600 space-y-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Free Online File Tools — PDF, Excel &amp; More
              </h2>
              <p>
                FileNesto is a free, browser-based platform offering 15 online file conversion and editing tools across PDF and Excel formats. Every tool runs entirely in your session — files are processed in memory and discarded immediately after your download. No account, no installation, no data stored on our servers.
              </p>

              <h3 className="text-lg font-semibold text-slate-800 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
                12 Free PDF Tools
              </h3>
              <p>
                FileNesto covers the full spectrum of PDF operations. <strong>Merge PDF</strong> lets you combine any number of documents into a single file — perfect for assembling reports, contracts, or study notes. <strong>Compress PDF</strong> reduces file size by optimising content streams without resampling images, keeping text crisp and documents readable. <strong>Split PDF</strong> extracts individual pages or custom ranges into separate files delivered as a ZIP archive.
              </p>
              <p>
                Need to edit the content? <strong>PDF to Word</strong> converts your PDF into an editable .docx file compatible with Microsoft Word, Google Docs, and LibreOffice. <strong>Word to PDF</strong> does the reverse — transforming any .docx document into a universally readable PDF in seconds. For data extraction, <strong>PDF to Excel</strong> detects and lifts tables from every PDF page into a structured .xlsx workbook, while <strong>Extract Text</strong> pulls the full text content into a plain .txt file.
              </p>
              <p>
                Visual workflows are covered too. <strong>PDF to Image</strong> converts every page to a high-quality JPEG at 150 DPI, bundled in a ZIP for convenient download. <strong>PDF Preview</strong> generates a thumbnail of the first page — ideal for checking documents before sending. Rounding out the toolkit: <strong>Rotate PDF</strong> (90°/180°/270°, all or specific pages), <strong>Delete PDF Pages</strong> (remove any page by number or range), and <strong>Protect PDF</strong> (add password encryption to any document).
              </p>

              <h3 className="text-lg font-semibold text-slate-800 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
                3 Free Excel Tools
              </h3>
              <p>
                FileNesto's Excel toolkit covers the three most common spreadsheet conversion needs. <strong>Excel to PDF</strong> converts any .xlsx spreadsheet into a clean, shareable PDF — ideal for sending financial reports or inventory lists to colleagues who don't have Excel. <strong>Excel to CSV</strong> exports the active sheet as a UTF-8 encoded CSV, ready for import into databases, BI platforms like Tableau or Power BI, Python/pandas scripts, or any data pipeline. <strong>CSV to Excel</strong> converts CSV files back into a full .xlsx workbook, automatically detecting UTF-8, UTF-8 BOM, and latin-1 encodings.
              </p>

              <h3 className="text-lg font-semibold text-slate-800 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Security &amp; Privacy by Design
              </h3>
              <p>
                Every file you upload to FileNesto is processed exclusively in memory within your session. Once your result is generated and downloaded, all file data is immediately wiped from the server. FileNesto does not log file contents, does not retain copies, and does not share data with third parties. No cookies tracking your uploads, no user profiles, no persistent storage. Your documents are yours — they never leave the processing pipeline.
              </p>

              <h3 className="text-lg font-semibold text-slate-800 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Works on Every Device
              </h3>
              <p>
                FileNesto is a fully responsive web application — it works on desktop browsers (Chrome, Firefox, Safari, Edge), tablets, and mobile devices running iOS or Android. No app to download, no plugin to install. Open filenesto.com, pick a tool, upload your file, and download the result. Each conversion typically completes in under 3 seconds for files up to 20MB.
              </p>

              <h3 className="text-lg font-semibold text-slate-800 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Coming Next — Word &amp; Image Tools
              </h3>
              <p>
                The next release will add Word document tools (merge .docx files, Word text extraction) and Image tools (image to PDF, compress JPG/PNG, convert HEIC to JPG). All new tools will follow the same free, secure, no-signup model as the existing 15 tools.
              </p>
            </div>
          )}
        </div>
      </section>

      </main>
      <Footer />
      <MobileStickyBar />
    </div>
  );
}
